import { useStrings } from "@1js/localization-components";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  shorthands,
  Tooltip,
} from "@fluentui/react-components";
import { Dismiss32Regular } from "@fluentui/react-icons";
import * as React from "react";
import { CloseTooltip } from "./PromptDescription.web.string";
import type { PromptDetailBaseProps } from "./PromptDetail.web.data";
import { PromptDetailTitle } from "./PromptDetailTitle.web";
import { PromptPreview } from "./PromptPreview/PromptPreview.web";

const useStyles = makeStyles({
  dialog: {
    "--margin": "6.5rem",
    maxWidth: "calc(100% - var(--margin))",
    maxHeight: "calc(100% - var(--margin))",
    display: "flex",
    width: "52.875rem",
    ...shorthands.borderRadius("1.5rem"),
    ...shorthands.padding("3rem"),
    "@media (max-width: 540px)": {
      ...shorthands.borderRadius("0px"),
      ...shorthands.padding("24px"),
      maxWidth: "100vw",
      maxHeight: "100%",
    },
  },

  title: {
    paddingBottom: "1rem",
  },

  dialogContent: {
    display: "flex",
    alignItems: "flex-start",
    flexShrink: 0,
    maxHeight: "50rem",
    paddingRight: "2.5rem",
    scrollbarWidth: "thin",
    ...shorthands.gap("2.5rem"),
    "@media (max-width: 540px)": {
      flexDirection: "column",
      alignSelf: "stretch",
      ...shorthands.gap("3rem"),
      ...shorthands.padding("0rem"),
    },
  },
});

export type PromptDetailProps = PromptDetailBaseProps & {
  dialogOpen?: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDialogClose: () => void;
};

export const PromptDetailModal: React.FC<PromptDetailProps> = ({
  prompt,
  morePrompts,
  isBookmarkEnabled,
  isPromptSavingEnabled,
  isPromptSharingEnabled,
  isPromptSharingViaEmailEnabled,
  isShowPromptAuthor,
  isPromptCopyLinkEnabled,
  dialogOpen,
  onBookmarkToggled,
  onShareClicked,
  handleDialogClose,
  setDialogOpen,
  onCopyClicked,
  onClicked,
  onDelete,
  onTryInAppBadgeAction,
  isPromptSharingToWorkgroupEnabled,
  onShareToWorkgroupClicked,
  groups,
}) => {
  const styles = useStyles();
  const localizations = useStrings({
    CloseTooltip,
  });

  if (!prompt) return null;

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(_event, data) => {
        if (!data.open) {
          handleDialogClose();
        }
        setDialogOpen(data.open);
      }}
    >
      <DialogSurface
        className={styles.dialog}
        role="dialog"
        aria-labelledby="dialog-title-id"
      >
        <DialogBody>
          <DialogTitle
            id="dialog-title-id"
            className={styles.title}
            action={
              <Tooltip
                content={localizations.CloseTooltip()}
                relationship="label"
              >
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label="close"
                    icon={<Dismiss32Regular />}
                  />
                </DialogTrigger>
              </Tooltip>
            }
          >
            <PromptDetailTitle {...prompt} />
          </DialogTitle>
          <DialogContent id="dialog" className={styles.dialogContent}>
            <PromptPreview
              prompt={prompt}
              morePrompts={morePrompts}
              onClick={onClicked}
              isBookmarked={prompt.UserActivity?.Favorite?.IsFavorite}
              isBookmarkEnabled={isBookmarkEnabled}
              isPromptSavingEnabled={isPromptSavingEnabled}
              isPromptSharingEnabled={isPromptSharingEnabled}
              isPromptSharingViaEmailEnabled={isPromptSharingViaEmailEnabled}
              isShowPromptAuthor={isShowPromptAuthor}
              isPromptCopyLinkEnabled={isPromptCopyLinkEnabled}
              onBookmarkToggled={onBookmarkToggled}
              onShareClicked={onShareClicked}
              onCopyClicked={onCopyClicked}
              onDelete={onDelete}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isPromptSharingToWorkgroupEnabled={
                isPromptSharingToWorkgroupEnabled
              }
              onShareToWorkgroupClicked={onShareToWorkgroupClicked}
              groups={groups}
            />
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
